/**
 * Advice register (compliance-grade SOA storage): every SOA generated for a REAL client file is
 * also written to the server's versioned register — immutable, numbered, attributed to the
 * licensee, and recorded on the tamper-evident audit trail. The in-app copy remains the working
 * view; the register is the legal record.
 */
import axios from 'axios';
import { apiBaseUrl } from './apiBase.ts';
import { getAccessToken } from './authClient.ts';

/** aged_care | support_at_home — see server/src/rest/advice/adviceRepo.ts's AdviceModule. */
export type AdviceModule = 'aged_care' | 'support_at_home' | 'retirement_village' | 'downsizer' | 'granny_flat' | 'cashflow_pension';

export interface RegisterSoaResult {
  soaId: string;
  version: number;
  licenseeName: string | null;
  afslNumber: string | null;
}

function getApiBaseUrl(): string {
  // One source for this — see services/apiBase.ts for why eight copies was a problem.
  return apiBaseUrl();
}

async function authHeaders(): Promise<{ Authorization: string; 'Content-Type': string }> {
  const token = await getAccessToken();
  if (!token) throw new Error('Not signed in');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

/** Register a newly generated SOA. Returns { soaId, version, licenseeName, afslNumber }. */
export async function registerSoa(profileId: string, module: AdviceModule, document: Record<string, unknown>): Promise<RegisterSoaResult> {
  const headers = await authHeaders();
  const res = await axios.post(
    `${getApiBaseUrl()}/rest/profiles/${profileId}/soas`,
    { module, document },
    { headers, timeout: 60_000 },
  );
  return res.data;
}

/**
 * Register advice a signed-in client generated on their OWN file (the direct channel). They are never
 * given their file's id, so this uses the id-free route; the server attaches it to the file they own.
 */
export async function registerOwnSoa(module: AdviceModule, document: Record<string, unknown>): Promise<RegisterSoaResult> {
  const headers = await authHeaders();
  const res = await axios.post(`${getApiBaseUrl()}/rest/profiles/mine/soas`, { module, document }, { headers, timeout: 60_000 });
  return res.data;
}

/** Mark a registered SOA as published (the only mutation the register allows). */
export async function publishRegisteredSoa(soaId: string): Promise<{ ok: boolean; version: number }> {
  const headers = await authHeaders();
  const res = await axios.post(`${getApiBaseUrl()}/rest/soas/${soaId}/publish`, {}, { headers, timeout: 30_000 });
  return res.data;
}

/** One version in the advice register, as the list endpoint returns it (document fetched separately). */
export interface RegisteredSoaSummary {
  id: string;
  profileId: string;
  module: AdviceModule;
  version: number;
  status: 'draft' | 'published';
  licenseeName: string | null;
  afslNumber: string | null;
  publishedAt: string | null;
  createdAt: string;
}

/**
 * Every SOA version ever generated for a client file, newest first.
 *
 * The register is the legal record; the in-app copy in sessionStorage is only the working view. The
 * Partner Portal used to list SOAs from that working copy alone, so signing out made a client's
 * generated advice vanish from Reports & SOAs even though it was safely stored server-side.
 */
export async function listSoas(profileId: string, module?: AdviceModule): Promise<RegisteredSoaSummary[]> {
  const headers = await authHeaders();
  const res = await axios.get(`${getApiBaseUrl()}/rest/profiles/${profileId}/soas`, {
    headers,
    params: module ? { module } : undefined,
    timeout: 30_000,
  });
  return res.data?.soas ?? [];
}

/** A saved modelling snapshot, as the list endpoint returns it (payload fetched separately). */
export interface SavedModelSummary {
  id: string;
  module: AdviceModule;
  label: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Save a modelling snapshot against a REAL client file.
 *
 * Saved strategies used to live ONLY in sessionStorage, so an adviser's modelling vanished at
 * sign-out and never reached the server at all — which is also why the Partner Portal could not tell
 * a client who had been fully modelled from one nobody had opened (`saved_models` was empty for every
 * client in the database). The server has always had the table and the route; nothing called it.
 *
 * Pass `modelId` to overwrite an existing snapshot instead of adding another.
 */
export async function saveModel(
  profileId: string,
  module: AdviceModule,
  label: string,
  payload: Record<string, unknown>,
  modelId?: string,
): Promise<{ modelId: string }> {
  const headers = await authHeaders();
  const res = await axios.post(
    `${getApiBaseUrl()}/rest/profiles/${profileId}/models`,
    { module, label, payload, ...(modelId ? { modelId } : {}) },
    { headers, timeout: 60_000 },
  );
  return res.data;
}

/** List the saved modelling snapshots for a profile, newest first. */
export async function listModels(profileId: string, module?: AdviceModule): Promise<SavedModelSummary[]> {
  const headers = await authHeaders();
  const res = await axios.get(`${getApiBaseUrl()}/rest/profiles/${profileId}/models`, {
    headers,
    params: module ? { module } : undefined,
    timeout: 30_000,
  });
  return res.data?.models ?? [];
}
